import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getDataTestId } from 'eone-ui/functions/data-test-ids';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BasicRecord, SCAdvanceTableProps } from './types';

interface PortalMenuItem {
  Label?: string;
  Icon?: IconProp;
  OnClick?: (id: string, row: any) => void;
  Disabled?: boolean;
  Divider?: boolean;
  Visible?: boolean;
}

interface PortalState {
  items: PortalMenuItem[];
  rect: DOMRect;
  rowId: string;
  row: any;
  parentTitle: string;
}

const menuViewportMargin = 8;
const menuAnchorGap = 4;

function getPortalMenuPosition(rect: DOMRect, viewportHeight: number) {
  const spaceAbove = Math.max(1, rect.top - menuViewportMargin - menuAnchorGap);
  const spaceBelow = Math.max(1, viewportHeight - rect.bottom - menuViewportMargin - menuAnchorGap);
  const openUp = spaceAbove > spaceBelow;

  return {
    wrapperStyle: {
      position: 'fixed',
      ...(openUp
        ? { bottom: viewportHeight - rect.top + menuAnchorGap }
        : { top: rect.bottom + menuAnchorGap }),
      left: rect.right,
      transform: 'translateX(-100%)',
      zIndex: 9999,
      width: 'max-content',
    } satisfies React.CSSProperties,
    menuStyle: {
      maxHeight: openUp ? spaceAbove : spaceBelow,
      overflowY: 'auto',
    } satisfies React.CSSProperties,
  };
}

/**
 * Workaround for eone-ui's TableRowButton not supporting MenuItems dropdowns.
 *
 * Strips MenuItems from RowButtons (so eone-ui renders a plain button),
 * and replaces OnClick with a handler that opens a portal-based dropdown
 * rendered using eone-ui's own CSS classes so it looks identical.
 */
function useMenuButtonPortal<D extends BasicRecord>(
  rowButtons: SCAdvanceTableProps<D>['RowButtons'],
  tableDataTestId?: string,
): {
  strippedButtons: SCAdvanceTableProps<D>['RowButtons'];
  portalElement: React.ReactNode;
} {
  const [portalState, setPortalState] = useState<PortalState | null>(null);

  const close = useCallback(() => setPortalState(null), []);

  // Close on any outside mousedown
  useEffect(() => {
    if (!portalState) return;
    const handler = () => close();
    // Defer by one tick so the opening click doesn't immediately close it
    const timer = setTimeout(() => window.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', handler);
    };
  }, [portalState, close]);

  // Strip MenuItems and replace OnClick with a portal opener
  const strippedButtons = useMemo(() => {
    return rowButtons?.map((button) => {
      if (!button.MenuItems?.length) return button;

      const menuItems = button.MenuItems;
      const parentTitle = button.Title;
      return {
        ...button,
        MenuItems: undefined,
        OnClick: (idField: string, row: D) => {
          const anchor = document.activeElement?.getBoundingClientRect();
          if (!anchor) return;
          setPortalState({
            items: menuItems,
            rect: anchor,
            rowId: idField,
            row,
            parentTitle,
          });
        },
      };
    });
  }, [rowButtons]) as SCAdvanceTableProps<D>['RowButtons'];

  const portalPosition = portalState
    ? getPortalMenuPosition(portalState.rect, window.innerHeight)
    : null;

  const portalElement = portalState
    ? createPortal(
        <div style={portalPosition?.wrapperStyle} onMouseDown={(e) => e.stopPropagation()}>
          {/* Reuse eone-ui's dropdown CSS classes for identical appearance.
              Must be a div (not span) — the CSS sets background/border/shadow
              which require block-level display to render correctly. */}
          <div className="eone-ui-button-drop-down-menu" style={portalPosition?.menuStyle}>
            <div className="eone-ui-button-drop-down-menu-container">
              {portalState.items
                .filter((item) => item.Visible !== false)
                .map((item, i) => {
                  if (item.Divider) {
                    return <div key={i} className="eone-ui-button-drop-down-menu-divider" />;
                  }

                  const handleClick = () => {
                    close();
                    item.OnClick?.(portalState.rowId, portalState.row);
                  };

                  const itemTestId = getDataTestId(
                    tableDataTestId,
                    portalState.rowId,
                    portalState.parentTitle,
                    item.Label,
                  );

                  if (item.Disabled) {
                    return (
                      <button
                        key={i}
                        type="button"
                        className="eone-ui-button-drop-down-menu-item-disabled"
                        data-testid={itemTestId}
                        disabled
                      >
                        {item.Icon && (
                          <div className="eone-ui-button-drop-down-menu-item-icon">
                            <FontAwesomeIcon icon={item.Icon as IconProp} fixedWidth />
                          </div>
                        )}
                        <div className="eone-ui-button-drop-down-menu-item-label">{item.Label}</div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={i}
                      type="button"
                      className="eone-ui-button-drop-down-menu-item"
                      data-testid={itemTestId}
                      onClick={handleClick}
                    >
                      {item.Icon && (
                        <div className="eone-ui-button-drop-down-menu-item-icon">
                          <FontAwesomeIcon icon={item.Icon as IconProp} fixedWidth />
                        </div>
                      )}
                      <div className="eone-ui-button-drop-down-menu-item-label">{item.Label}</div>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return { strippedButtons, portalElement };
}

export { useMenuButtonPortal };
